import React, {useState} from "react";
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import NotificationAddIcon from '@mui/icons-material/NotificationAdd';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EventIcon from '@mui/icons-material/Event';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Tooltip from '@mui/material/Tooltip';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import SyncIcon from '@mui/icons-material/Sync';
import AlertsModal from "./AlertsModal";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import CSVDownloadModal from "./CSVDownloadModal";

dayjs.extend(utc);
dayjs.extend(timezone);

const Toolbar = props => {
    const [alertsModalOpen, setAlertsModalOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [csvModalOpen, setcsvModalOpen] = useState(false);
    const open = Boolean(anchorEl);

    const handleDropdownClick = e => {
        setAnchorEl(e.currentTarget);
    }

    const handleClose = () => {
        setAnchorEl(null);
    }

    const renderUnitValue = value => {
        if(value === 'impgal') return 'imp. gal.';
        else if(value === 'impgal/min') return 'imp. gal./min';
        return value;
    }

    return (
        <>
        {alertsModalOpen && <AlertsModal alertsModalOpen={alertsModalOpen} setAlertsModalOpen={setAlertsModalOpen} />}
        {csvModalOpen &&
            <CSVDownloadModal
                siteID={props.siteID}
                csvModalOpen={csvModalOpen}
                setcsvModalOpen={setcsvModalOpen}
                selectedDates={props.selectedDates}
                setSelectedDates={props.setSelectedDates}
                plotSpecs={props.plotSpecs}
                setPlotSpecs={props.setPlotSpecs}
                loggedUnits={props.loggedUnits}
                data={props.data}
                siteName={props.siteName}
                useV2RawData={props.useV2RawData}
            />
        }
        
        <div className="chart-graph-controls">
            <div style={{flex:'4'}} className="toolbar-units">
                <Box sx={{ width: '95%'}}>
                    <FormControl fullWidth margin="dense" disabled={props.plotSpecCooldown && (props.data == null ? true : props.data.length === 0)}>
                        <InputLabel id="demo-simple-select-label">Units</InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={props.plotSpecs.metricType}
                            label="Metric"
                            
                            onChange={e => props.updatePlotSpecs(e.target.value, 'metricOptions')}
                        >
                            {props.plotSpecs.metricOptions.map((value, _) => (
                                <MenuItem value={value}>{renderUnitValue(value)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </div>

            <div style={{flex:'4'}} className="toolbar-timeframe">
                <Box sx={{ width: '95%'}}>
                    <FormControl fullWidth margin="dense" disabled={props.plotSpecCooldown && (props.data == null ? true : props.data.length === 0)}>
                        <InputLabel id="demo-simple-select-label">Timeframe</InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={props.plotSpecs.timeframe}
                            label="Timeframe"
                            onChange={e => {
                                if(e.target.value !== 'custom'){
                                    props.updatePlotSpecs(e, 'timeframe')
                                }
                            }}
                        >
                            <MenuItem value='4h' style={{display: props.aircompressors ? 'none' : 'block'}}>4 hours</MenuItem>
                            <MenuItem value='1d' style={{display: props.aircompressors ? 'none' : 'block'}}>Day</MenuItem>
                            <MenuItem value='7d'>7 Days</MenuItem>
                            <MenuItem value='14d' style={{display: props.aircompressors ? 'block' : 'none'}}>2 Weeks</MenuItem>
                            <MenuItem value='31d'>Month</MenuItem>
                            <MenuItem value='365d'>Year</MenuItem>
                            <MenuItem value='custom' style={{display:'none'}}>Custom</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </div>

            <div style={{flex:'6'}} className="toolbar-start">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                        disabled={props.plotSpecCooldown && (props.data == null ? true : props.data.length === 0)}
                        label="Start"
                        ampm={false}
                        ampmInClock={false}
                        value={props.selectedDates.start}
                        sx={{ width: '95%' }}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                margin: 'dense',
                                inputProps: { readOnly: true }
                            }
                        }}
                        format={`YYYY-MM-DD HH:mm [${props.selectedDates.startTzName ?? ''}]`}
                        disableFuture
                        maxDateTime={
                            props.selectedDates.end
                            ? props.selectedDates.end.subtract(1, 'minute')
                            : undefined
                        }
                        timezone={props.selectedDates.tzName}
                        onChange={value => {
                            if(!value || !value.isValid()) return;

                            const end = props.selectedDates.end;
                            if(end == null || value.valueOf() < end){
                                props.setSelectedDates(prev => ({
                                    ...prev,
                                    ['start']: value,
                                    refreshReady: end != null
                                }));
                            }else{
                                props.setSelectedDates(prev => ({
                                    ...prev,
                                    refreshReady: false
                                }));
                            }
                        }}
                    />
                </LocalizationProvider>
            </div>

            <div style={{flex:'6'}} className="toolbar-end">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                        disabled={props.plotSpecCooldown && (props.data == null ? true : props.data.length === 0)}
                        label="End"
                        ampm={false}
                        ampmInClock={false}
                        value={props.selectedDates.end}
                        sx={{ width: '95%' }}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                margin: 'dense',
                                inputProps: { readOnly: true }
                            }
                        }}
                        format={`YYYY-MM-DD HH:mm [${props.selectedDates.endTzName ?? ''}]`}
                        disableFuture
                        minDateTime={
                            props.selectedDates.start
                            ? props.selectedDates.start.add(1, 'minute')
                            : undefined
                        }
                        timezone={props.selectedDates.tzName}
                        onChange={value => {
                            if(!value || !value.isValid()) return;

                            const start = props.selectedDates.start;
                            if(start == null || value.valueOf() > start){
                                props.setSelectedDates(prev => ({
                                    ...prev,
                                    ['end']: value,
                                    refreshReady: start != null
                                }))
                            }else{
                                props.setSelectedDates(prev => ({
                                    ...prev,
                                    refreshReady: false
                                }));
                            }
                        }}
                    />
                </LocalizationProvider>
            </div>

            <div style={{flex:'4'}} className="toolbar-resolution">
                <Box sx={{ width: '95%'}}>
                    <FormControl fullWidth margin="dense" disabled={props.plotSpecCooldown && (props.data == null ? true : props.data.length === 0)}>
                        <InputLabel id="demo-simple-select-label">Resolution</InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={props.plotSpecs.resolution}
                            label="Resolution"
                            onChange={e => props.updatePlotSpecs(e, 'resolution')}
                        >
                            {
                                (
                                    props.plotSpecs.resolution === '1m' || 
                                    ['1h','4h','1d'].includes(props.plotSpecs.timeframe) ||
                                    (props.plotSpecs.timeframe === 'custom' && Math.abs(props.selectedDates.end?.diff(props.selectedDates.start, 'day', true)) <= 3)
                                ) && <MenuItem value='1m'>1 min</MenuItem>
                            }
                            {
                                (
                                    props.plotSpecs.resolution === '5m' || 
                                    ['1h','4h','1d'].includes(props.plotSpecs.timeframe) || 
                                    (props.plotSpecs.timeframe === 'custom' && Math.abs(props.selectedDates.end?.diff(props.selectedDates.start, 'day', true)) <= 3 && Math.abs(props.selectedDates.end?.diff(props.selectedDates.start, 'minute', true)) >= 30)
                                ) && <MenuItem value='5m'>5 min</MenuItem>
                            }
                            {
                                (
                                    props.plotSpecs.resolution === '15m' || 
                                    ['4h','1d'].includes(props.plotSpecs.timeframe) || 
                                    (props.plotSpecs.timeframe === 'custom' && Math.abs(props.selectedDates.end?.diff(props.selectedDates.start, 'day', true)) <= 7 && Math.abs(props.selectedDates.end?.diff(props.selectedDates.start, 'minute', true)) >= 60)
                                ) && <MenuItem value='15m'>15 min</MenuItem>
                            }
                            {
                                (
                                    props.plotSpecs.resolution === '1h' || 
                                    ['1d','7d','14d','31d'].includes(props.plotSpecs.timeframe) || 
                                    (props.plotSpecs.timeframe === 'custom' && Math.abs(props.selectedDates.end?.diff(props.selectedDates.start, 'day', true)) <= 30 && Math.abs(props.selectedDates.end?.diff(props.selectedDates.start, 'minute', true)) >= 360)
                                ) && <MenuItem value='1h'>1 hour</MenuItem>
                            }
                            {
                                (
                                    props.plotSpecs.resolution === '1d' || 
                                    ['7d','14d','31d','365d'].includes(props.plotSpecs.timeframe) || 
                                    (props.plotSpecs.timeframe === 'custom' && Math.abs(props.selectedDates.end?.diff(props.selectedDates.start, 'day', true)) >= 7 && Math.abs(props.selectedDates.end?.diff(props.selectedDates.start, 'minute', true)) >= 2880)
                                ) && <MenuItem value='1d'>1 day</MenuItem>
                            }
                        </Select>
                    </FormControl>
                </Box>
            </div>

            <div style={{flex:'1', display:'flex', flexDirection:'row'}} className="toolbar-refresh">
                <Tooltip title="Refresh graph" slotProps={{popper: {sx: { zIndex: 1000000 }}}}>
                    <IconButton disabled={!props.selectedDates.refreshReady} style={{flex:'1', borderRadius:'0'}} color="info" onClick={() => {
                        props.updatePlotSpecs({target: {value: 'custom'}}, 'timeframe')
                    }}>
                        <SyncIcon/>
                    </IconButton>
                </Tooltip>
            </div>

            <div style={{flex:'1', display:'flex', flexDirection:'row'}} className="toolbar-tools">
                <Tooltip title="Tools" slotProps={{popper: {sx: { zIndex: 1000000 }}}}>
                    <IconButton style={{flex:'1', borderRadius:'0'}} color="info" onClick={handleDropdownClick}>
                        <MoreVertIcon/>
                    </IconButton>
                </Tooltip>
                <Menu
                    anchorEl={anchorEl}
                    id="account-menu"
                    open={open}
                    onClose={handleClose}
                    onClick={handleClose}
                    disableScrollLock
                    slotProps={{
                        paper: {
                            elevation: 0,
                            sx: {
                            overflow: 'visible',
                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                            mt: 1.5,
                            '& .MuiAvatar-root': {
                                width: 32,
                                height: 32,
                                ml: -0.5,
                                mr: 1,
                            },
                            '&::before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                right: 14,
                                width: 10,
                                height: 10,
                                bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                            },
                            },
                        }
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                    <MenuItem onClick={() => {
                        handleClose();
                        setAlertsModalOpen(true);
                    }}>
                        <ListItemIcon>
                            <NotificationAddIcon color="info" fontSize="small" />
                        </ListItemIcon>
                        Manage alerts
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleClose();
                        props.setEventsModalOpen(true)
                    }}>
                        <ListItemIcon>
                            <EventIcon color="info" fontSize="small" />
                        </ListItemIcon>
                        Add event
                    </MenuItem>
                    <MenuItem onClick={() => {
                            handleClose();
                            setcsvModalOpen(true);
                    }}>
                        <ListItemIcon>
                            <FileDownloadIcon color="info" fontSize="small" />
                        </ListItemIcon>
                        Download CSV
                    </MenuItem>
                </Menu>
            </div>
        </div>
        </>
    );
}

export default Toolbar;
